/**
 * Association tables for the CSV Import Review summary.
 *
 * - PendingAssociationsTable: grouped table of detected associations.
 * - UnmatchedAssociationsTable: rows where the join key had no match.
 *
 * Extracted from SummaryView.tsx to keep the main file under 300 lines.
 */

import { useCallback, useMemo, useState } from 'react';

import { Button } from '@/lib/base/buttons/button';
import { Table, TableCard } from '@/lib/base/table';
import type { AssociationsData, CsvImport } from '@/lib/csv-import/api';

import { getEntityTypeLabel } from '../../utils/entityTypeLabels';
import { getFileDisplayName } from '../../utils/fileDisplayName';
import {
  formatRowNumbersHint,
  getEntityRowDisplayName,
} from '../summaryHelpers';
import { getPropertyLabel } from './types';

// Stable empty maps for getPropertyLabel's fallback prettification (hoisted so
// the reference is stable across renders).
const EMPTY_SCHEMA_MAP = {};
const EMPTY_NEW_PROPERTY_LABELS = {};

/**
 * Humanize an internal column name for display. Import machinery uses hidden
 * transient columns (`_owner_email`, `_lf_note_company_ref`) and raw property
 * names (`ahoy_email`) — neither should reach the user verbatim. Strips
 * leading underscores, then reuses getPropertyLabel's prefix-strip + Title
 * Case fallback.
 */
// eslint-disable-next-line react-refresh/only-export-components -- shared helper used by this file's tables and by the extracted InvalidAssociationsTable
export function prettifyColumnName(name: string): string {
  return getPropertyLabel(
    name.replace(/^_+/, ''),
    EMPTY_SCHEMA_MAP,
    EMPTY_NEW_PROPERTY_LABELS
  );
}

// ============================================================================
// AssociationNavigateButton
// ============================================================================

interface AssociationNavigateButtonProps {
  rowId: string;
  label: string;
  onNavigate: (rowId: string) => void;
}

/**
 * Renders an underlined link-like button that, when clicked, asks the parent
 * SummaryView to expand the right category accordion and scroll to the row
 * whose id matches `rowId`. Uses the design-system Button in `link-gray`
 * mode so focus/hover states stay consistent with the rest of the product.
 */
function AssociationNavigateButton({
  rowId,
  label,
  onNavigate,
}: AssociationNavigateButtonProps) {
  const handleClick = useCallback(() => onNavigate(rowId), [onNavigate, rowId]);
  return (
    <Button
      color='link-gray'
      size='sm'
      onClick={handleClick}
      className='text-fg-primary hover:text-fg-brand-primary'
    >
      {label}
    </Button>
  );
}

// ============================================================================
// PendingAssociationsTable
// ============================================================================

interface PendingAssociationRow {
  id: string;
  // Human-friendly entity display name (e.g. "Sarah Nolan" for a contact,
  // "Anthropic" for a company). Falls back to filename + row when no
  // identifying property is present in the row's data.
  sourceName: string;
  sourceEntityLabel: string;
  sourceFile: string;
  sourceRowId: string;
  sourceRow: number;
  targetName: string;
  targetEntityLabel: string;
  targetFile: string;
  targetRowId: string;
  targetRow: number;
  // The value(s) used as the join key. Source and target are usually
  // identical (that's why they matched) but we keep them separate so subtle
  // casing/whitespace differences are still visible.
  matchedOn: string;
}

/** Grouped representation of associations that share the same entity display
 *  names + match value. A single CSV often produces multiple raw associations
 *  that look identical to the user (e.g. five rows all naming the same
 *  company by the same domain). Grouping collapses those into one visible
 *  row, and preserves the underlying row ids so the user can still navigate
 *  to a specific record. Multiplicity is conveyed by the per-side row-numbers
 *  hint ("Rows 1, 3, 7") rather than a dedicated count column. */
interface PendingAssociationGroup {
  /** Stable key for React (and for the navigation handler's first hop). */
  key: string;
  /** Display-only entry — the first member of the group. We render its
   *  source/target names and use its rowIds for the navigation click. */
  sample: PendingAssociationRow;
  /** Sorted, unique 1-indexed source row numbers contributing to this
   *  group. Surfaced as the "Rows N, M" hint under each side. */
  sourceRows: number[];
  targetRows: number[];
}

export interface PendingAssociationsTableProps {
  pendingAssociations: AssociationsData['pending_associations'];
  importData: CsvImport;
  getEntityTypeLabel: (name: string) => string;
  /** Called when the user clicks a Source or Target file cell. Receives the
   *  row id (`${fileId}-${row_index}`) of the record to navigate to. */
  onNavigate?: (rowId: string) => void;
}

/**
 * Renders pending associations as a small table inside the Detected accordion.
 * Looks up the matched column value for each side via `association_mappings`
 * (which knows the join column) + `analyzed_rows.data` (which holds the row's
 * values keyed by CSV column name).
 */
export const INITIAL_GROUP_CAP = 25;
export const GROUP_CAP_INCREMENT = 100;

export function PendingAssociationsTable({
  pendingAssociations,
  importData,
  getEntityTypeLabel,
  onNavigate,
}: PendingAssociationsTableProps) {
  const [displayCap, setDisplayCap] = useState(INITIAL_GROUP_CAP);
  const handleShowMore = useCallback(() => {
    setDisplayCap(prev => prev + GROUP_CAP_INCREMENT);
  }, []);

  // Shared fileId → entity_type_name lookup — used by both tableRows and
  // entityPairLabel to avoid rebuilding the same map in multiple memos.
  const fileToEntityType = useMemo(() => {
    const map: Record<string, string> = {};
    for (const file of importData.uploaded_files) {
      map[String(file.file_id)] = file.entity_type_name;
    }
    return map;
  }, [importData.uploaded_files]);

  const tableRows = useMemo<PendingAssociationRow[]>(() => {
    // Index each file's analyzed rows by row_index for fast access. We keep
    // both `raw_data` (keyed by CSV column name) and `data` (keyed by CRM
    // property name) because the association may use a CSV column that
    // isn't mapped to a CRM property (e.g. a foreign-key column like
    // "Associated Company"). Backend join logic in analyzer.py uses
    // raw_data first; we mirror that here.
    interface RowLookup {
      data: Record<string, unknown>;
      rawData: Record<string, unknown>;
    }
    const analyzedByFile: Record<string, Record<number, RowLookup>> = {};
    for (const [fileId, fileResult] of Object.entries(
      importData.analysis_result ?? {}
    )) {
      if (
        typeof fileResult === 'object' &&
        fileResult !== null &&
        'analyzed_rows' in fileResult
      ) {
        const rows = (
          fileResult as {
            analyzed_rows: Array<{
              row_index: number;
              data: Record<string, unknown>;
              raw_data?: Record<string, unknown>;
            }>;
          }
        ).analyzed_rows;
        const indexed: Record<number, RowLookup> = {};
        for (const r of rows) {
          indexed[r.row_index] = {
            data: r.data ?? {},
            rawData: r.raw_data ?? {},
          };
        }
        analyzedByFile[fileId] = indexed;
      }
    }

    // For each pending association, resolve the matched value on each side.
    // Resolution order per side:
    //   1. raw_data[csv_column]           (mirrors the backend join)
    //   2. data[mapped property name]    (via column_mappings — covers cases
    //                                     where raw_data isn't present, e.g.
    //                                     older imports)
    //   3. em-dash                        (truly missing)
    const resolveValue = (
      rowLookup: RowLookup | undefined,
      csvColumn: string | undefined,
      columnMappingsForFile: Record<string, string>
    ): string => {
      if (!rowLookup || !csvColumn) {
        return '—';
      }
      const fromRaw = rowLookup.rawData[csvColumn];
      if (fromRaw !== undefined && fromRaw !== null && fromRaw !== '') {
        return String(fromRaw);
      }
      const propertyName = columnMappingsForFile[csvColumn];
      if (propertyName) {
        const fromMapped = rowLookup.data[propertyName];
        if (
          fromMapped !== undefined &&
          fromMapped !== null &&
          fromMapped !== ''
        ) {
          return String(fromMapped);
        }
      }
      return '—';
    };

    return pendingAssociations.map(assoc => {
      const mapping = importData.association_mappings.find(
        m =>
          String(m.source_file_id) === String(assoc.source_file_id) &&
          String(m.target_file_id) === String(assoc.target_file_id)
      );
      const sourceLookup =
        analyzedByFile[String(assoc.source_file_id)]?.[assoc.source_row_index];
      const targetLookup =
        analyzedByFile[String(assoc.target_file_id)]?.[assoc.target_row_index];
      const sourceColumnMappings =
        importData.column_mappings[String(assoc.source_file_id)] ?? {};
      const targetColumnMappings =
        importData.column_mappings[String(assoc.target_file_id)] ?? {};

      const sourceValue = resolveValue(
        sourceLookup,
        mapping?.source_column,
        sourceColumnMappings
      );
      const targetValue = resolveValue(
        targetLookup,
        mapping?.target_column,
        targetColumnMappings
      );

      // Compute the entity display name on each side. Fall back to a "Row N"
      // label when no identifying property is available on the row.
      const sourceEntityType =
        fileToEntityType[String(assoc.source_file_id)] ?? '';
      const targetEntityType =
        fileToEntityType[String(assoc.target_file_id)] ?? '';
      const sourceName =
        (sourceLookup &&
          getEntityRowDisplayName(
            sourceLookup.data,
            sourceLookup.rawData,
            sourceEntityType
          )) ||
        `Row ${assoc.source_row_index + 1}`;
      const targetName =
        (targetLookup &&
          getEntityRowDisplayName(
            targetLookup.data,
            targetLookup.rawData,
            targetEntityType
          )) ||
        `Row ${assoc.target_row_index + 1}`;

      // Show a single "Matched on" string. Use the source value; surface the
      // target value too if it differs (rare, but case/whitespace variations
      // are useful to spot).
      const matchedOn =
        sourceValue === targetValue || targetValue === '—'
          ? sourceValue
          : `${sourceValue} ↔ ${targetValue}`;

      return {
        id: `${assoc.source_file_id}-${assoc.source_row_index}-${assoc.target_file_id}-${assoc.target_row_index}`,
        sourceName,
        sourceEntityLabel: sourceEntityType
          ? getEntityTypeLabel(sourceEntityType)
          : 'Source',
        sourceFile: getFileDisplayName(
          String(assoc.source_file_id),
          importData.uploaded_files
        ),
        // rowId matches the format used by rowsByCategory in the main
        // SummaryView (`${fileId}-${row_index}`) so onNavigate can find the
        // record across categories.
        sourceRowId: `${assoc.source_file_id}-${assoc.source_row_index}`,
        sourceRow: assoc.source_row_index + 1,
        targetName,
        targetEntityLabel: targetEntityType
          ? getEntityTypeLabel(targetEntityType)
          : 'Target',
        targetFile: getFileDisplayName(
          String(assoc.target_file_id),
          importData.uploaded_files
        ),
        targetRowId: `${assoc.target_file_id}-${assoc.target_row_index}`,
        targetRow: assoc.target_row_index + 1,
        matchedOn,
      };
    });
  }, [pendingAssociations, importData, fileToEntityType, getEntityTypeLabel]);

  // Group rows that share the same display name pair + match value. A CSV
  // commonly produces many "duplicate-looking" associations — different
  // source rows joining to the same target, or repeated source rows in the
  // input file. The user sees those as duplicates, so we collapse them into
  // a single visual row with a list of the underlying row numbers.
  const tableGroups = useMemo<PendingAssociationGroup[]>(() => {
    const map = new Map<string, PendingAssociationGroup>();
    for (const row of tableRows) {
      // The entity-pair labels are part of the identity: two associations
      // from DIFFERENT pairs whose display names coincide (a note titled
      // like a contact, fallback "Row N" names) are distinct rows, not
      // duplicates — collapsing them would hide one association entirely.
      const key = `${row.sourceEntityLabel}→${row.targetEntityLabel} ${row.sourceName} ${row.targetName} ${row.matchedOn}`;
      const existing = map.get(key);
      if (existing) {
        if (!existing.sourceRows.includes(row.sourceRow)) {
          existing.sourceRows.push(row.sourceRow);
        }
        if (!existing.targetRows.includes(row.targetRow)) {
          existing.targetRows.push(row.targetRow);
        }
      } else {
        map.set(key, {
          key,
          sample: row,
          sourceRows: [row.sourceRow],
          targetRows: [row.targetRow],
        });
      }
    }
    // Sort row-number lists so the hint reads naturally (e.g. "Rows 1, 3, 7").
    for (const group of map.values()) {
      group.sourceRows.sort((a, b) => a - b);
      group.targetRows.sort((a, b) => a - b);
    }
    return Array.from(map.values());
  }, [tableRows]);

  // Column header labels. Only claim a specific entity type when EVERY group
  // agrees on it — a multi-type import (e.g. Contact → Company rows alongside
  // Note → Contact rows) renders in ONE table, and stamping the first pair's
  // labels over mixed rows mislabels every other pair. When mixed, fall back
  // to generic Source/Target; each row still shows its own entity type in the
  // per-side sub-label (via getFileDisplayName's entity-type fallback).
  const headerLabels = useMemo(() => {
    const sourceLabels = new Set(
      tableGroups.map(group => group.sample.sourceEntityLabel)
    );
    const targetLabels = new Set(
      tableGroups.map(group => group.sample.targetEntityLabel)
    );
    const source = sourceLabels.size === 1 ? [...sourceLabels][0] : 'Source';
    const target = targetLabels.size === 1 ? [...targetLabels][0] : 'Target';
    return { source, target };
  }, [tableGroups]);

  // Accessible table label — specific ("Contact → Company") only when the
  // header labels are, generic otherwise.
  const entityPairLabel =
    headerLabels.source !== 'Source' && headerLabels.target !== 'Target'
      ? `${headerLabels.source} → ${headerLabels.target}`
      : '';

  const isCapped = tableGroups.length > displayCap;
  const visibleGroups = useMemo(
    () => (isCapped ? tableGroups.slice(0, displayCap) : tableGroups),
    [tableGroups, displayCap, isCapped]
  );

  if (tableGroups.length === 0) {
    return (
      <p className='text-xs text-fg-tertiary text-center py-2'>
        No association records to display.
      </p>
    );
  }

  return (
    <div className='border border-border-secondary rounded-lg overflow-hidden bg-bg-primary'>
      <TableCard.ScrollArea>
        <Table
          size='sm'
          aria-label={entityPairLabel || 'Detected associations'}
        >
          <Table.Header>
            <Table.Row>
              <Table.Head label={headerLabels.source} isRowHeader />
              <Table.Head label={headerLabels.target} />
            </Table.Row>
          </Table.Header>
          {/* Static body children — header uses static columns too, so both
              sides stay in the same react-aria-components collection mode.
              IMPORTANT: do NOT pass `id` to <Table.Row> in static mode —
              presence of `id` flips react-aria into dynamic-collection mode
              and it can no longer find the static columns, surfacing as
              "Cell count must match column count. Found N cells and 0 columns".
              React's `key` is sufficient for static rows. */}
          <Table.Body>
            {visibleGroups.map(group => (
              <Table.Row key={group.key}>
                <Table.Cell>
                  <div className='flex flex-col gap-0.5'>
                    {onNavigate ? (
                      <AssociationNavigateButton
                        rowId={group.sample.sourceRowId}
                        label={group.sample.sourceName}
                        onNavigate={onNavigate}
                      />
                    ) : (
                      <span className='text-fg-primary'>
                        {group.sample.sourceName}
                      </span>
                    )}
                    <span className='text-xs text-fg-quaternary truncate'>
                      {group.sample.sourceFile} ·{' '}
                      {formatRowNumbersHint(group.sourceRows)}
                    </span>
                  </div>
                </Table.Cell>
                <Table.Cell>
                  <div className='flex flex-col gap-0.5'>
                    {onNavigate ? (
                      <AssociationNavigateButton
                        rowId={group.sample.targetRowId}
                        label={group.sample.targetName}
                        onNavigate={onNavigate}
                      />
                    ) : (
                      <span className='text-fg-primary'>
                        {group.sample.targetName}
                      </span>
                    )}
                    <span className='text-xs text-fg-quaternary truncate'>
                      {group.sample.targetFile} ·{' '}
                      {formatRowNumbersHint(group.targetRows)}
                    </span>
                  </div>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      </TableCard.ScrollArea>
      {isCapped && (
        <div className='border-t border-border-secondary px-4 py-2 text-center'>
          <Button color='link-gray' size='sm' onClick={handleShowMore}>
            Show{' '}
            {Math.min(GROUP_CAP_INCREMENT, tableGroups.length - displayCap)}{' '}
            more of {tableGroups.length} groups
          </Button>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// UnmatchedAssociationsTable
// ============================================================================

interface UnmatchedAssociationRow {
  id: string;
  sourceFile: string;
  /** Sorted, unique 1-indexed source row numbers that all missed with this
   *  same (column, value, target) — one visual row per distinct miss. */
  sourceRows: number[];
  sourceColumn: string;
  sourceValue: string;
  targetFile: string;
  targetColumn: string;
}

export interface UnmatchedAssociationsTableProps {
  unmatchedAssociations: AssociationsData['unmatched_associations'];
  importData: CsvImport;
}

/**
 * Renders unmatched associations as a small table inside the Unmatched
 * accordion. The unmatched payload already carries the value that couldn't
 * be matched (source_value) plus the column it was being looked up against
 * (target_column) — no analyzed_rows lookup needed.
 */
export function UnmatchedAssociationsTable({
  unmatchedAssociations,
  importData,
}: UnmatchedAssociationsTableProps) {
  const [displayCap, setDisplayCap] = useState(INITIAL_GROUP_CAP);
  const handleShowMore = useCallback(() => {
    setDisplayCap(prev => prev + GROUP_CAP_INCREMENT);
  }, []);

  const tableRows = useMemo<UnmatchedAssociationRow[]>(() => {
    // Group identical misses: several source rows failing to match the same
    // value against the same target are one problem, not N — render one row
    // with the contributing row numbers, mirroring PendingAssociationsTable.
    const groups = new Map<string, UnmatchedAssociationRow>();
    for (const u of unmatchedAssociations) {
      const sourceFile = getFileDisplayName(
        String(u.source_file_id),
        importData.uploaded_files
      );
      // The two rails look for their target in different places: another CSV,
      // or a record already in the CRM. Same two columns describe both —
      // "where we looked" and "what we matched on". Internal column names are
      // humanized: transient underscore columns and raw property names mean
      // nothing to the user.
      const target =
        u.target_entity_type_name === undefined
          ? {
              targetFile: getFileDisplayName(
                String(u.target_file_id),
                importData.uploaded_files
              ),
              targetColumn: prettifyColumnName(u.target_column),
            }
          : {
              targetFile: getEntityTypeLabel(u.target_entity_type_name),
              targetColumn: prettifyColumnName(u.target_property),
            };
      const key = `${sourceFile}|${u.source_column}|${u.source_value}|${target.targetFile}|${target.targetColumn}`;
      const existing = groups.get(key);
      const rowNumber = u.source_row_index + 1;
      if (existing) {
        if (!existing.sourceRows.includes(rowNumber)) {
          existing.sourceRows.push(rowNumber);
        }
      } else {
        groups.set(key, {
          id: key,
          sourceFile,
          sourceRows: [rowNumber],
          sourceColumn: prettifyColumnName(u.source_column),
          sourceValue: u.source_value,
          ...target,
        });
      }
    }
    for (const group of groups.values()) {
      group.sourceRows.sort((a, b) => a - b);
    }
    return Array.from(groups.values());
  }, [unmatchedAssociations, importData.uploaded_files]);

  const isCapped = tableRows.length > displayCap;
  const visibleRows = useMemo(
    () => (isCapped ? tableRows.slice(0, displayCap) : tableRows),
    [tableRows, displayCap, isCapped]
  );

  if (tableRows.length === 0) {
    return (
      <p className='text-xs text-fg-tertiary text-center py-2'>
        No unmatched associations to display.
      </p>
    );
  }

  return (
    <div className='border border-border-secondary rounded-lg overflow-hidden bg-bg-primary'>
      <TableCard.ScrollArea>
        <Table size='sm' aria-label='Unmatched associations'>
          <Table.Header>
            <Table.Row>
              <Table.Head label='Source' isRowHeader />
              <Table.Head label='Rows' />
              <Table.Head label='Source column' />
              <Table.Head label='Unmatched value' />
              <Table.Head label='Target' />
              <Table.Head label='Matched against' />
            </Table.Row>
          </Table.Header>
          {/* Static body to match the static header (see PendingAssociationsTable
              for the explanation of why we don't use id/items in static mode). */}
          <Table.Body>
            {visibleRows.map(row => (
              <Table.Row key={row.id}>
                <Table.Cell>{row.sourceFile}</Table.Cell>
                <Table.Cell className='tabular-nums'>
                  {formatRowNumbersHint(row.sourceRows)}
                </Table.Cell>
                <Table.Cell>{row.sourceColumn}</Table.Cell>
                <Table.Cell className='text-utility-warning-700'>
                  {row.sourceValue || '—'}
                </Table.Cell>
                <Table.Cell>{row.targetFile}</Table.Cell>
                <Table.Cell>{row.targetColumn}</Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      </TableCard.ScrollArea>
      {isCapped && (
        <div className='border-t border-border-secondary px-4 py-2 text-center'>
          <Button color='link-gray' size='sm' onClick={handleShowMore}>
            Show {Math.min(GROUP_CAP_INCREMENT, tableRows.length - displayCap)}{' '}
            more of {tableRows.length} rows
          </Button>
        </div>
      )}
    </div>
  );
}
